import React, { useState, useEffect } from 'react';
import { Tag, Loading, Empty, Picker, Popup } from 'react-vant';
import { ArrowLeft, ArrowDown, CalendarO, Friends } from '@react-vant/icons';
import { useNavigate } from 'react-router-dom';
import request from '../../api/request';
import dayjs from 'dayjs';

const SportsSchedule = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [schedules, setSchedules] = useState([]);
  const [meets, setMeets] = useState([]);
  const [currentMeet, setCurrentMeet] = useState(null);
  const [showPicker, setShowPicker] = useState(false);

  useEffect(() => {
    fetchMeets();
  }, []);

  const fetchMeets = async () => {
    try {
      // 同时获取进行中/筹备中运动会与已归档往届运动会
      const [activeRes, archivedRes] = await Promise.allSettled([
        request.get('/sports/meets/', { params: { is_archived: false, page_size: 50 } }),
        request.get('/sports/meets/', { params: { is_archived: true, page_size: 50 } })
      ]);
      const activeList = activeRes.status === 'fulfilled' ? (activeRes.value?.items || []) : [];
      const archivedList = archivedRes.status === 'fulfilled' ? (archivedRes.value?.items || []) : [];
      
      const allMeets = [
        ...activeList,
        ...archivedList.filter(a => !activeList.some(act => act.id === a.id))
      ];

      setMeets(allMeets.map(m => ({
        text: m.is_archived ? `${m.name} (往届)` : m.name,
        value: m.id
      })));

      if (allMeets.length > 0) {
        // 优先选中活跃中的运动会，若无则选中第一项
        const defaultMeet = allMeets.find(m => !m.is_archived) || allMeets[0];
        setCurrentMeet(defaultMeet);
        fetchSchedules(defaultMeet.id);
      }
    } catch (error) {
      console.error('获取运动会列表失败', error);
    }
  };

  const fetchSchedules = async (meetId) => {
    setLoading(true);
    try {
      const res = await request.get('/sports/schedules/', {
        params: { sports_meet_id: meetId, page_size: 100 }
      });
      setSchedules(res.items || []);
    } catch (error) {
      console.error('获取日程失败', error);
    } finally {
      setLoading(false);
    }
  };

  const onPickerConfirm = (val, opt) => {
    setCurrentMeet({ id: opt.value, name: opt.text });
    setShowPicker(false);
    fetchSchedules(opt.value);
  };

  return (
    <div style={{ minHeight: '100vh', background: 'var(--canvas-bg, #f8fafc)', paddingBottom: 60 }}>
      {/* 沉浸式星瀚空天蓝页眉 */}
      <div style={{
        background: 'linear-gradient(135deg, #055e43 0%, #076f51 50%, #0c8c68 100%)',
        padding: '44px 16px 20px',
        color: '#fff',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* 装饰半透圆 */}
        <div style={{
          position: 'absolute', top: -30, right: -30,
          width: 140, height: 140, borderRadius: '50%',
          background: 'rgba(255, 255, 255, 0.08)'
        }} />

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'relative', zIndex: 1 }}>
          <div
            onClick={() => navigate(-1)}
            style={{
              width: 36, height: 36, borderRadius: '50%',
              background: 'rgba(255,255,255,0.2)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: 'pointer'
            }}
          >
            <ArrowLeft color="#fff" fontSize={18} />
          </div>

          <div style={{ textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <h1 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#fff', letterSpacing: '-0.2px' }}>
              比赛日程
            </h1>
            <div
              onClick={() => setShowPicker(true)}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 4,
                background: 'rgba(255, 255, 255, 0.2)',
                backdropFilter: 'blur(8px)',
                padding: '3px 10px',
                borderRadius: 999,
                fontSize: 12,
                color: '#ffffff',
                cursor: 'pointer',
                marginTop: 4,
                maxWidth: 220
              }}
            >
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {currentMeet?.name || '选择运动会'}
              </span>
              <ArrowDown fontSize={11} style={{ opacity: 0.8 }} />
            </div>
          </div>

          <div
            onClick={() => setShowPicker(true)}
            style={{
              width: 36, height: 36, borderRadius: '50%',
              background: 'rgba(255,255,255,0.2)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: 'pointer'
            }}
            title="选择运动会"
          >
            <CalendarO color="#fff" fontSize={18} />
          </div>
        </div>
      </div>

      <div style={{ padding: '16px 12px 16px 0', minHeight: 'calc(100vh - 120px)' }}>
        {loading && schedules.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '60px 0' }}>
            <Loading type="spinner" color="#056b4f" vertical>加载日程中...</Loading>
          </div>
        ) : schedules.length > 0 ? (
          <div>
            {schedules.map((item) => {
              const isFinished = item.status === 'completed';
              return (
                <div key={item.id} style={{ display: 'flex', marginBottom: 6 }}>
                  {/* Time Column */}
                  <div style={{ width: 74, display: 'flex', flexDirection: 'column', alignItems: 'center', paddingTop: 10 }}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: '#0f172a', marginBottom: 8 }}>
                      {dayjs(item.scheduled_time).format('HH:mm')}
                    </div>
                    <div style={{ 
                      width: 9, height: 9, borderRadius: '50%', 
                      background: isFinished ? '#94a3b8' : '#056b4f',
                      boxShadow: isFinished ? 'none' : '0 0 0 3px #e8f6f0',
                      zIndex: 2, position: 'relative'
                    }} />
                    <div style={{ flex: 1, width: 2, background: '#e2e8f0', marginTop: 4 }} />
                  </div>

                  {/* Content Card */}
                  <div style={{ 
                    flex: 1,
                    background: '#ffffff',
                    borderRadius: 16,
                    padding: 16,
                    marginBottom: 14,
                    boxShadow: 'var(--shadow-card, 0 4px 16px rgba(15, 23, 42, 0.05))',
                    border: '1px solid #f1f5f9'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                      <div style={{ fontSize: 16, fontWeight: 700, color: '#0f172a', flex: 1, marginRight: 8 }}>
                        {item.eventName || item.event_name || item.project_name || '项目加载中...'}
                      </div>
                      <Tag
                        round
                        style={{
                          background: isFinished ? '#f1f5f9' : '#e8f9f0',
                          color: isFinished ? '#94a3b8' : '#00b578',
                          border: 'none',
                          fontWeight: 600,
                          padding: '2px 8px'
                        }}
                      >
                        {isFinished ? '已结束' : '待进行'}
                      </Tag>
                    </div>
                    <div style={{ fontSize: 13, color: '#64748b', marginBottom: 12 }}>
                      {item.round_name || '决赛'} · {item.group_name || '第 1 组'}
                    </div>
                    <div style={{ display: 'flex', gap: 16, paddingTop: 10, borderTop: '1px solid #f1f5f9' }}>
                      <div style={{ display: 'flex', alignItems: 'center', fontSize: 12, color: '#64748b' }}>
                        <span style={{ marginRight: 4 }}>📍</span> {item.venue || '跑道'}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', fontSize: 12, color: '#64748b' }}>
                        <span style={{ marginRight: 4 }}>👥</span> {item.participant_count || 0} 人参赛
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div style={{ padding: '60px 0' }}>
            <Empty description="暂无赛程安排" />
          </div>
        )}
      </div>

      <Popup visible={showPicker} round position="bottom">
        <Picker
          title="选择运动会"
          columns={meets}
          onConfirm={onPickerConfirm}
          onCancel={() => setShowPicker(false)}
        />
      </Popup>
    </div>
  );
};

export default SportsSchedule;
